import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Lock, UserCheck, Share2, Clock, Mail, AlertCircle } from 'lucide-react';

export default function Privacy() {
  const navigate = useNavigate();
  const lastUpdated = "February 8, 2026";

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-dark-bg/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-neon-purple" />
            <h1 className="text-xl font-bold">Privacy Policy</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-dark-surface rounded-2xl p-6 md:p-8 border border-white/10">
          {/* Last Updated */}
          <p className="text-white/60 text-sm mb-6">Last Updated: {lastUpdated}</p>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-purple mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Introduction
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              vgoride ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains 
              how we collect, use, disclose, and safeguard your information when you use our ride-hailing platform 
              and related services (the "Service").
            </p>
            <p className="text-white/80 leading-relaxed mb-4">
              This Privacy Policy is designed to comply with applicable United States federal laws, including the 
              Federal Trade Commission (FTC) Act and guidelines, as well as Kansas state privacy laws.
            </p>
            <p className="text-white/80 leading-relaxed">
              By using our Service, you consent to the data practices described in this Privacy Policy. If you do 
              not agree with the terms of this Privacy Policy, please do not access or use the Service.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-purple mb-4 flex items-center gap-2">
              <Database className="w-5 h-5" />
              Information We Collect
            </h2>
            
            <h3 className="text-lg font-semibold text-white mb-3">Personal Information</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              When you register for an account or use our Service, we may collect the following personal information:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4 mb-6">
              <li><strong>Contact Information:</strong> Name, email address, phone number</li>
              <li><strong>Account Information:</strong> Username, password (encrypted)</li>
              <li><strong>Profile Information:</strong> Profile picture (optional)</li>
              <li><strong>Driver Information:</strong> Driver's license number, vehicle information, license plate number (for drivers only)</li>
            </ul>

            <h3 className="text-lg font-semibold text-white mb-3">Location Data</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              To provide our ride-hailing services, we collect location data including:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4 mb-6">
              <li>Pickup and dropoff locations for ride requests</li>
              <li>Real-time location during active rides (for both riders and drivers)</li>
              <li>Location data to display nearby available drivers</li>
            </ul>

            <h3 className="text-lg font-semibold text-white mb-3">Usage Data</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              We automatically collect certain information about your use of the Service:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
              <li>Ride history and trip details</li>
              <li>Communication records between riders and drivers</li>
              <li>Device information (browser type, operating system)</li>
              <li>Log data (access times, pages viewed)</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-purple mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              How We Use Your Information
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
              <li><strong>Service Delivery:</strong> To facilitate ride requests, connect riders with drivers, and provide transportation services</li>
              <li><strong>Account Management:</strong> To create and manage your account, verify your identity, and maintain account security</li>
              <li><strong>Communication:</strong> To send service-related notifications, respond to inquiries, and provide customer support</li>
              <li><strong>Safety and Security:</strong> To detect and prevent fraud, abuse, and security incidents</li>
              <li><strong>Service Improvement:</strong> To analyze usage patterns and improve our platform and services</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-purple mb-4 flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Information Sharing and Disclosure
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your 
              information only in the following circumstances:
            </p>
            
            <h3 className="text-lg font-semibold text-white mb-3">With Other Users</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              When you request or provide a ride, certain information is shared between riders and drivers to 
              facilitate the service, including:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4 mb-6">
              <li>Name and profile picture</li>
              <li>Pickup and dropoff locations</li>
              <li>Real-time location during rides</li>
              <li>Vehicle information (for drivers)</li>
            </ul>

            <h3 className="text-lg font-semibold text-white mb-3">Legal Requirements</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              We may disclose your information if required to do so by law or in response to valid requests by 
              public authorities (e.g., court orders, subpoenas, or government regulations).
            </p>

            <h3 className="text-lg font-semibold text-white mb-3">Business Transfers</h3>
            <p className="text-white/80 leading-relaxed">
              In the event of a merger, acquisition, or sale of assets, your information may be transferred as 
              part of that transaction. We will notify you of any such change in ownership or control of your 
              personal information.
            </p>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-purple mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Data Security
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              We implement appropriate technical and organizational security measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
              <li>Encryption of passwords and sensitive data</li>
              <li>Secure HTTPS connections for all data transmission</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls limiting employee access to personal data</li>
            </ul>
            <p className="text-white/80 leading-relaxed mt-4">
              While we strive to protect your personal information, no method of transmission over the Internet 
              or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-purple mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Data Retention
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              We retain your personal information for as long as your account is active or as needed to provide 
              you with our services. We may also retain and use your information as necessary to:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
              <li>Comply with legal obligations</li>
              <li>Resolve disputes</li>
              <li>Enforce our agreements</li>
              <li>Maintain business records as required by law</li>
            </ul>
            <p className="text-white/80 leading-relaxed mt-4">
              Ride history and trip data may be retained for a period of up to 7 years for legal and regulatory 
              compliance purposes.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-purple mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              Your Rights and Choices
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              You have certain rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
              <li><strong>Access:</strong> You can request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> You can update or correct your account information at any time through your account settings</li>
              <li><strong>Deletion:</strong> You can request deletion of your account and personal information by contacting us</li>
              <li><strong>Location Services:</strong> You can disable location services through your device settings, though this may limit functionality</li>
              <li><strong>Communications:</strong> You can opt out of non-essential communications by adjusting your notification preferences</li>
            </ul>
            <p className="text-white/80 leading-relaxed mt-4">
              To exercise any of these rights, please contact us at admin@vgoride.net.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-purple mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Children's Privacy
            </h2>
            <p className="text-white/80 leading-relaxed">
              Our Service is not intended for children under 18 years of age. We do not knowingly collect personal 
              information from children under 18. If we become aware that we have collected personal information 
              from a child under 18 without parental consent, we will take steps to delete that information. If 
              you believe we may have collected information from a child under 18, please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-neon-purple mb-4">
              Changes to This Privacy Policy
            </h2>
            <p className="text-white/80 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or for 
              legal, operational, or regulatory reasons. We will notify you of any material changes by posting 
              the updated Privacy Policy on our platform and updating the "Last Updated" date. We encourage you 
              to review this Privacy Policy periodically. Your continued use of the Service after any changes 
              indicates your acceptance of the updated Privacy Policy.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-4">
            <h2 className="text-xl font-bold text-neon-purple mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Contact Us
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us at:
            </p>
            <div className="bg-dark-bg rounded-xl p-4 border border-white/10">
              <p className="text-white/80"><strong>vgoride</strong></p>
              <p className="text-white/80">Privacy Inquiries: <a href="mailto:admin@vgoride.net" className="text-neon-purple hover:underline">admin@vgoride.net</a></p>
              <p className="text-white/80">General Support: <a href="mailto:customer-support@vgoride.net" className="text-neon-purple hover:underline">customer-support@vgoride.net</a></p>
            </div>
          </section>

          {/* Kansas Residents Notice */}
          <section className="mb-4 p-4 bg-neon-purple/10 rounded-xl border border-neon-purple/20">
            <h3 className="text-lg font-semibold text-neon-purple mb-2">Notice to Kansas Residents</h3>
            <p className="text-white/80 leading-relaxed text-sm">
              If you are a Kansas resident, you may have additional rights under the Kansas Consumer Protection Act. 
              vgoride complies with all applicable Kansas state laws regarding consumer privacy and data protection. 
              For questions specific to Kansas privacy laws, please contact us at admin@vgoride.net.
            </p>
          </section>

          {/* Footer Note */}
          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} vgoride. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
